import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { LabeledDropDownSelect } from "../DropDownSelect";

const searchAuditOperationsOptions = [
	{ value: "audit", label: "Audit Search Index (This Firm)" },
	{ value: "rebuild", label: "Delete and Rebuild Search Index (This Firm)" },
];
const operationNameLookup = {
	audit: 0,
	rebuild: 1,
};

import {
	rebuildSearchIndex,
	auditSearchIndex,
	auditAndReIndexMissingItems,
	accessSearchAuditPage,
	setAuditPageOperation,
} from "../../actions/adminActions";

import { openGlobalDialog } from "../../actions/globalEditDialogActions";

import { ButtonWithIcon } from "../ButtonWithIcon";

import {
	numberWithCommas,
	msToTime,
	mediumDateFormatMinusTimezone,
} from "../../utilities/utilities";
import { LexWorkplaceRefreshIcon } from "../LexWorkplaceIcons";

const SearchIndexDetailDisplay = ({ searchIndexDetail }) => {
	if (!searchIndexDetail) {
		return <div>Loading index details...</div>;
	}

	const { indices } = searchIndexDetail;

	return (
		<div>
			{indices.length === 0 ? (
				<div style={{ color: "var(--lw-darker-red)" }}>
					No indices found
				</div>
			) : (
				<table
					style={{
						borderCollapse: "collapse",
						width: "100%",
						maxWidth: 800,
					}}
				>
					<thead>
						<tr
							style={{
								borderBottom: "2px solid #cbcbcb",
								textAlign: "left",
							}}
						>
							<th style={{ padding: "6px 12px" }}>Index Name</th>
							<th style={{ padding: "6px 12px" }}>Documents</th>
							<th style={{ padding: "6px 12px" }}>Shards</th>
							<th style={{ padding: "6px 12px" }}>Aliases</th>
							<th style={{ padding: "6px 12px" }}>Active</th>
						</tr>
					</thead>
					<tbody>
						{indices.map((idx) => (
							<tr
								key={idx.indexName}
								style={{
									borderBottom: "1px solid #e0e0e0",
									backgroundColor: idx.isActive
										? "#e8f5e9"
										: "transparent",
								}}
							>
								<td
									style={{
										padding: "6px 12px",
										fontFamily: "monospace",
									}}
								>
									{idx.indexName}
								</td>
								<td style={{ padding: "6px 12px" }}>
									{numberWithCommas(idx.docsCount)}
								</td>
								<td style={{ padding: "6px 12px" }}>
									{idx.shardCount}
								</td>
								<td
									style={{
										padding: "6px 12px",
										fontFamily: "monospace",
									}}
								>
									{idx.aliases.length > 0
										? idx.aliases.join(", ")
										: "-"}
								</td>
								<td style={{ padding: "6px 12px" }}>
									{idx.isActive ? "Yes" : "No"}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</div>
	);
};

const BulkJobTimeDisplay = ({ startTime, endTime }) => {
	const startDate = new Date(startTime);
	const endDate = new Date(endTime);

	const durationMS = endDate - startDate;

	return (
		<div>
			<div>{`Job Started on: ${mediumDateFormatMinusTimezone(
				startDate
			)}`}</div>
			<div>{`Elapsed Time: ${msToTime(durationMS)}`}</div>
		</div>
	);
};

const AuditResultsDisplay = ({
	auditFailed,
	searchAuditResults,
	searchAuditFailedData,
	jobMetadata,
	indexMissingItemsHandler,
}) => {
	return auditFailed ? (
		<div>
			<h4>Audit Operation Results</h4>
			<div>Audit Failed</div>
			<div>{JSON.stringify(searchAuditFailedData)}</div>
			<div>{JSON.stringify(jobMetadata)}</div>
		</div>
	) : (
		<AuditSuccessResults
			{...searchAuditResults}
			jobMetadata={jobMetadata}
			indexMissingItemsHandler={indexMissingItemsHandler}
		/>
	);
};
const AuditSuccessResults = ({
	missingItemsCount,
	allSearchResults,
	jobMetadata,
	indexMissingItemsHandler,
}) => {
	const keyMap = [
		{ keyname: "documents", noun: "Documents" },
		{ keyname: "documentVersions", noun: "Document Versions" },
		{ keyname: "emails", noun: "Emails" },
		{ keyname: "matterNotes", noun: "Matter Notes" },
	];

	var sumOfMissingItems = 0;

	return (
		<div style={{ display: "flex" }}>
			<div style={{ flex: "0 0 50%" }}>
				<h4>Audit Operation Results</h4>
				<div>Audit Succeeded</div>
				<BulkJobTimeDisplay {...jobMetadata} />
				<div style={{ marginTop: 8 }}>
					{keyMap.map(({ keyname, noun }) => {
						const numberOfMissingItems = missingItemsCount[keyname];
						sumOfMissingItems += numberOfMissingItems;
						return (
							<div
								key={keyname}
								style={{ display: "flex", gap: 16 }}
							>
								<div
									style={{ width: 200 }}
								>{`Missing ${noun}:`}</div>
								<div style={{ width: 300 }}>
									{`${numberWithCommas(
										numberOfMissingItems
									)} (${numberWithCommas(
										allSearchResults[keyname]
											? allSearchResults[keyname]
											: 0
									)} total in Search Index)`}
								</div>
							</div>
						);
					})}
				</div>
				<div style={{ marginTop: 16 }}>
					<ButtonWithIcon
						handler={indexMissingItemsHandler}
						disabled={sumOfMissingItems === 0}
						buttonText="Add Missing Items to Index"
						iconComponent={<LexWorkplaceRefreshIcon />}
					/>
				</div>
			</div>
			<div style={{ flex: "0 0 50%" }}>
				<div style={{ marginTop: 32 }}>
					<div>
						{`Missing items can be indexed by pressing the button "Add Missing Items to Index".
					 Progress of the re-index job can be monitored in the
					"Batch Jobs" tab for this Firm.`}
					</div>
				</div>
			</div>
		</div>
	);
};

function AdminSearchIndexManagement({
	tenantID,
	tenantName,
	searchIndexDetail,
}) {
	const dispatch = useDispatch();

	const auditResultsLoaded = useSelector(
		(state) => state.searchAudit.resultsLoaded
	);
	const searchAuditResults = useSelector(
		(state) => state.searchAudit.auditResults
	);
	const auditFailed = useSelector((state) => state.searchAudit.auditFailed);
	const searchAuditFailedData = useSelector(
		(state) => state.searchAudit.auditFailedData
	);
	const jobMetadata = useSelector((state) => state.searchAudit.jobMetadata);
	const operation = useSelector(
		(state) => state.searchAudit.activeOperationOnAuditPage
	);

	useEffect(() => {
		if (tenantID) {
			dispatch(accessSearchAuditPage(tenantID));
		}
	}, [tenantID, dispatch]);

	const handleChange = useCallback(
		(key, { value }) => {
			dispatch(setAuditPageOperation(value));
		},
		[dispatch]
	);

	const rebuildSearchIndexIntent = useCallback(() => {
		dispatch(
			openGlobalDialog(
				{
					label: "Delete and Rebuild Search Index?",
					mainText: `This will completely delete and rebuild the search index for firm:"${tenantName}", tenantID:${tenantID}.  To proceed, please type "rebuild" in the text field.`,
					value: "",
					submitButtonLabel: "Rebuild",
				},
				() => {
					dispatch(rebuildSearchIndex(tenantID))
						.then((res) => console.log(res))
						.catch((err) => console.log(err));
				},
				(state) => {
					return {
						canSubmit: state.value === "rebuild",
						erroMessage: "",
					};
				}
			)
		);
	}, [dispatch, tenantName, tenantID]);

	const goButtonPressed = useCallback(() => {
		if (operation === "audit") {
			dispatch(auditSearchIndex(tenantID));
		} else if (operation === "rebuild") {
			rebuildSearchIndexIntent();
		}
	}, [operation, tenantID, dispatch, rebuildSearchIndexIntent]);

	return (
		<div
			style={{
				padding: 12,
				paddingLeft: 32,
				paddingRight: 32,
				display: "flex",
				flexDirection: "column",
				gap: 32,
			}}
		>
			<SearchIndexDetailDisplay searchIndexDetail={searchIndexDetail} />
			<div>
				<div style={{ display: "flex", width: "100%" }}>
					<div style={{ flex: "0 0 50%" }}>
						<h3>Search Index Operations</h3>
						<div style={{ display: "flex" }}>
							<div style={{ width: 400, marginRight: 24 }}>
								<LabeledDropDownSelect
									options={searchAuditOperationsOptions}
									value={
										searchAuditOperationsOptions[
											operationNameLookup[operation]
										]
									}
									name="operation"
									handler={handleChange}
								/>
							</div>
							<div>
								<ButtonWithIcon
									handler={goButtonPressed}
									iconComponent={<LexWorkplaceRefreshIcon />}
									buttonText="Go"
								/>
							</div>
						</div>
					</div>{" "}
					<div style={{ flex: "0 0 50%" }}>
						<div style={{ marginTop: 24, marginBottom: 48 }}>
							<h4>Audit Search Index</h4>
							<div>
								This process will look for Documents, Document
								Versions, Emails, and Matter Notes that exist in
								storage but are missing from the search index.
								Once complete, you will have the opportunity to
								add missing items to the search index.
							</div>
						</div>
						<div>
							<h4>Delete and re-build Search Index for Firm</h4>
							<div>
								This process will completely delete and re-build
								the search index from items in storage. This
								process can take a long time and should be
								considered a last resort
							</div>
						</div>
					</div>
				</div>

				<div
					style={{
						marginTop: 16,
						marginBottom: 16,
						borderBottom: "1px solid #cbcbcb",
					}}
				/>

				{auditResultsLoaded && (
					<AuditResultsDisplay
						searchAuditResults={searchAuditResults}
						auditFailed={auditFailed}
						searchAuditFailedData={searchAuditFailedData}
						jobMetadata={jobMetadata}
						indexMissingItemsHandler={() =>
							dispatch(auditAndReIndexMissingItems(tenantID))
						}
					/>
				)}
			</div>
		</div>
	);
}

export default AdminSearchIndexManagement;
